import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import type { SignInInput } from '../../services/auth-service/types';
import { useAuth } from '../../hooks/useAuth';
import { useAuthStore } from '../../store/auth-store';
import { ErrorMessage } from '../inputs/error-message';
import { InputField } from '../inputs/input-field';
import { Button } from '../inputs/button';
import { PasswordField } from '../inputs/password-field';
import { errorMessage } from '../../utils';
import parse from 'html-react-parser';

interface Props {
  loginRedirect?: string;
}

export const LoginForm: React.FC<Props> = ({ loginRedirect }: Props) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignInInput>();
  const [success, setSuccess] = useState<boolean>(false);
  const { isAuthenticated } = useAuth();
  const { signIn, signInError } = useAuthStore();
  const navigate = useNavigate();
  const [signInErrorMessage, setSignInErrorMessage] =
    useState<string>(signInError);
  const onSubmit = async (data: SignInInput) => {
    try {
      await signIn(data.email, data.password);
      setSuccess(true);
    } catch (err) {
      const error = err as Error & { status?: string };
      let errMessage = errorMessage(error);

      if (errMessage === 'User is not confirmed.') {
        navigate('/auth/verify', { state: data });
      } else if (errMessage === 'Validation error') {
        errMessage =
          '<strong>Access Denied</strong> <br> You are not allowed to access this page at this time.';
      } else if (error.status === 'FORCE_CHANGE_PASSWORD') {
        const normalizedEmail = data.email.trim().toLowerCase();
        navigate('/auth/change-password', {
          state: {
            email: normalizedEmail,
            oldPassword: data.password,
            isForce: true,
            hideResetCode: true,
          },
        });
      }
      setSignInErrorMessage(errMessage);
      // handle signin error
    }
  };

  useEffect(() => {
    if (isAuthenticated && loginRedirect) {
      navigate(loginRedirect);
    }
  }, [isAuthenticated, loginRedirect, navigate]);

  const requiredFieldErrorMessage = 'This field is required';
  const loading = useMemo(
    () => isSubmitting || success,
    [isSubmitting, success],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {signInErrorMessage && (
        <ErrorMessage>{parse(signInErrorMessage)}</ErrorMessage>
      )}
      <fieldset>
        <InputField
          label="Email Address"
          type="email"
          errorMessage={errors.email?.message}
          {...register('email', {
            required: requiredFieldErrorMessage,
          })}
        />
      </fieldset>
      <fieldset className="mt-3">
        <PasswordField
          label="Password"
          errorMessage={errors.password?.message}
          {...register('password', {
            required: requiredFieldErrorMessage,
          })}
        />
      </fieldset>
      <fieldset className="flex flex-row justify-between mt-6">
        <div className="hidden">
          <p className="text-gray font-medium text-sm">
            Can&rsquo;t access the account?
          </p>
          {/* TODO: add official link */}
          <a href="/" className="font-medium text-blue hover:text-blue-dark">
            Contact IT Support
          </a>
        </div>
        <div className="mt-0 ml-1">
          <Link
            className="text-red font-medium font-inter"
            to="/auth/forgot-password">
            Forgot Password?
          </Link>
        </div>
        <div>&nbsp;</div>
        <Button className="md:flex-none flex-1" type="submit" loading={loading}>
          Login
        </Button>
      </fieldset>
    </form>
  );
};

LoginForm.defaultProps = {
  loginRedirect: '/',
};
